import { PublishingHouseModel } from 'ApiClient/metadata'
import isUndefined from 'lodash/fp/isUndefined'
import omitBy from 'lodash/fp/omitBy'

import { IsNullOrUndefined } from 'Common/utils/helpers/HelperGeneral'

type Claim = { name: string; value: string }
export type AccessMap = Record<string, Record<string, string[]>>
type AuthPublishingHouseGroup = { id: string; publishingHouseIds?: string[] }
export type OpusUserObject = {
    access: AccessMap
    claims: Claim[]
    publishingHouseGroups: AuthPublishingHouseGroup[]
    id?: string
    firstname?: string
    lastname?: string
    email?: string
    brands?: { id: string }[]
}

const publishingHouseRegex = /ph:([\w-]+)/
const publishingHouseGroupRegex = /phg:([\w-]+)/

export const getUserData: any = (user: any, publishingHouses: any, access?: AccessMap) => {
    const { profile, access_token } = user
    const opusUserObject = profile.opus_user_object
    return omitBy(isUndefined, {
        ...profile,
        token: access_token,
        opus_contact_id: convertUserDataStringsToNumberArray(profile.opus_contact_id),
        opus_user: getUserObjectWithFallback(user, opusUserObject, publishingHouses, access),
        opus_user_object: undefined,
    })
}

const convertUserDataStringsToNumberArray = (value: any) => {
    if (IsNullOrUndefined(value)) return []
    if (!Array.isArray(value)) return [parseInt(value, 10)]
    return value.map((v) => parseInt(v, 10))
}

const getUserObjectWithFallback = (
    user: any,
    opus_user_object: string,
    allPublishingHouses: any,
    access?: AccessMap,
): OpusUserObject | undefined => {
    if (opus_user_object) {
        return JSON.parse(opus_user_object)
    }

    return user.access_token
        ? getUserObjectFromAccessToken(user, allPublishingHouses, access)
        : undefined
}

const getUserObjectFromAccessToken = (
    user: any,
    allPublishingHouses: PublishingHouseModel[],
    accessFromApi?: AccessMap,
): OpusUserObject => {
    const claims = getClaims(user)
    const access =
        accessFromApi ?? (allPublishingHouses ? getAccess(claims, allPublishingHouses) : {})
    const publishingHouseGroups = allPublishingHouses
        ? getPublishingHouseGroups(claims, allPublishingHouses)
        : []

    return {
        access,
        claims,
        publishingHouseGroups,
    }
}

function getClaims(user: any): Claim[] {
    const accessToken = decodeAccessToken(user.access_token)

    const opusClaims = Object.fromEntries(
        Object.entries(accessToken).filter(([key]) => key.startsWith('opus_')),
    )

    return Object.entries(opusClaims).flatMap(([name, value]) =>
        Array.isArray(value) ? value.map((v) => ({ name, value: v })) : [{ name, value }],
    )
}

const getAccess = (claims: Claim[], allPublishingHouses: PublishingHouseModel[]): AccessMap => {
    const access: AccessMap = {}

    claims.forEach(({ name, value }) => {
        const [role, token] = value.split('|')
        if (!role || !token) return

        const publishingHouseMatch = publishingHouseRegex.exec(token)
        const publishingHouseGroupMatch = publishingHouseGroupRegex.exec(token)

        let targetPublishingHouseIds: string[] = []

        if (publishingHouseMatch) {
            targetPublishingHouseIds = [publishingHouseMatch[1]]
        } else if (publishingHouseGroupMatch) {
            const groupId = publishingHouseGroupMatch[1]
            targetPublishingHouseIds = allPublishingHouses
                .filter((ph) => ph.publishingHouseGroupId === groupId)
                .map((ph) => ph.id ?? '')
        }

        targetPublishingHouseIds.forEach((phId) => {
            if (!access[name]) access[name] = {}
            if (!access[name][phId]) access[name][phId] = []
            if (!access[name][phId].includes(role)) {
                access[name][phId].push(role)
            }
        })
    })

    return access
}

const getPublishingHouseGroups = (claims: Claim[], allPublishingHouses: PublishingHouseModel[]) => {
    const publishingHouseGroupIds = [
        ...new Set(
            claims
                .map((c) => publishingHouseGroupRegex.exec(c.value)?.[1]?.toLowerCase())
                .filter(Boolean) as string[],
        ),
    ]

    return publishingHouseGroupIds.map((id) => ({
        id,
        publishingHouseIds: allPublishingHouses
            .filter((ph) => (ph.publishingHouseGroupId ?? '').toLowerCase() === id)
            .map((ph) => (ph.id ?? '').toLowerCase()),
    }))
}

const decodeAccessToken = (accessToken: string): Record<string, any> => {
    const base64Url = accessToken.split('.')[1]
    if (!base64Url) throw new Error('Invalid JWT: no payload found')

    const base64 = base64Url
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), '=')

    const jsonPayload = atob(base64)
    return JSON.parse(jsonPayload)
}
